import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SyntheticEvent,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import ExcelJS from "exceljs";
import {
  facilityComplianceService,
  type EnforcementActionRecord,
  type EnforcementSearchResponse,
  type HistoricalPenaltyAnalysis,
  type HistoricalPenaltySomTagContext,
} from "@services/facilityCompliance";
import { riskAnalysisService, type SomSearchResponse } from "@services/riskAnalysis";
import { continuousComplianceService } from "@services/continuousCompliance";
import {
  ALL_REGULATORY_BODIES,
  buildEnforcementSearchRequestPayload,
  buildYearRange,
  defaultRegulatoryBodyOptions,
  enforcementContextFromSelectedFacility,
  enforcementRecordMatchesRegulatoryBody,
  isRegulatoryOpenDataArchiveRecord,
  formatDollarAmountsInText,
  formatHistoricalCurrency,
  formatSomTagChipLabel,
  getEnforcementPenaltyList,
  mergeRegulatoryAuthorityFilters,
  buildCfrEcfrLink,
  CFR_CITATION_IN_TEXT_RE,
  splitViolationCodeIntoTagsAndOther,
  truncateForExcelCell,
  type EnforcementFacilityContext,
  type HistoricExportRow,
} from "@utils/historicEnforcement";
import { getCurrentUserId, isGlobalAdministrator } from "@utils/userUtils";

const StyledAccordion = styled(Accordion)({
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: "0 0 8px 0" },
  "& .accordion-title": { color: "#262626", transition: "color 0.3s ease" },
  "&.Mui-expanded .accordion-title": { color: "#065B70" },
  "& .MuiAccordionSummary-root": { alignItems: "center" },
  "& .MuiAccordionSummary-content": { alignItems: "center", flexWrap: "nowrap" },
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Rethink Sans, sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const expandIconSvg = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="#262626"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const historicPenaltiesLeadingIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      stroke="#065b70"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function formatEnforcementSearchedDateLabel(data: EnforcementSearchResponse): string {
  const ts = data.search_timestamp;
  if (ts && typeof ts === "string") {
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  return new Date().toLocaleDateString();
}

const PERSISTED_DB_SNAPSHOT_SOURCE_LABEL = "Persisted DB snapshot";

function isPersistedDbSnapshotSourceLabel(value: unknown): boolean {
  if (typeof value !== "string") return false;
  return value.trim().toLowerCase() === PERSISTED_DB_SNAPSHOT_SOURCE_LABEL.toLowerCase();
}

/** External source names only; omits persisted snapshot entries from the rules payload. */
function enforcementExternalSourcesList(data: EnforcementSearchResponse | undefined): string[] {
  const raw = data?.sources_searched ?? [];
  return raw.filter((s) => typeof s === "string" && !isPersistedDbSnapshotSourceLabel(s));
}

function enforcementHasPersistedDbProvenance(data: EnforcementSearchResponse | undefined): boolean {
  if (!data) return false;
  if (data.persisted_hit === true) return true;
  return (data.sources_searched ?? []).some((s) => isPersistedDbSnapshotSourceLabel(s));
}

/**
 * Footer "Sources:" body (before " · Searched …"). Non–global admins never see the persisted
 * snapshot label; global admins see external sources plus ", Persisted DB snapshot" when provenance
 * includes the DB snapshot, or that label alone when the snapshot is the only source.
 */
function buildHistoricEnforcementSourcesDisplayText(data: EnforcementSearchResponse | undefined): string {
  const external = enforcementExternalSourcesList(data);
  const hasPersisted = enforcementHasPersistedDbProvenance(data);
  const fallback = "Federal, state, and local enforcement databases";

  if (isGlobalAdministrator() && hasPersisted) {
    if (external.length) {
      return `${external.join(", ")}, ${PERSISTED_DB_SNAPSHOT_SOURCE_LABEL}`;
    }
    return PERSISTED_DB_SNAPSHOT_SOURCE_LABEL;
  }

  return external.length ? external.join(", ") : fallback;
}

function sumFilteredEnforcementAmount(records: EnforcementActionRecord[]): number {
  return records.reduce((sum, record) => {
    const raw = record.amount;
    const numeric =
      typeof raw === "number" ? raw : parseFloat(String(raw ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(numeric) ? sum + numeric : sum;
  }, 0);
}

function enforcementPenaltyAmount(penalty: EnforcementActionRecord, countryDisplay: string): string {
  return (
    penalty.amount_formatted ||
    (penalty.amount != null ? formatHistoricalCurrency(penalty.amount, countryDisplay) : "N/A")
  );
}

function severityChipColor(sev: string | undefined): { bg: string; color: string } {
  const s = (sev || "").toLowerCase();
  if (s === "high") return { bg: "#fee2e2", color: "#991b1b" };
  if (s === "moderate") return { bg: "#ffedd5", color: "#9a3412" };
  return { bg: "#e0f4f8", color: "#065b70" };
}

function TextWithCfrLinks({ text, sx }: { text: string; sx?: object }) {
  const s = String(text);
  const re = new RegExp(CFR_CITATION_IN_TEXT_RE.source, CFR_CITATION_IN_TEXT_RE.flags);
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    const raw = m[0];
    const href = buildCfrEcfrLink(raw);
    parts.push(
      href ? (
        <Link
          key={`cfr-${m.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: "inherit", fontWeight: "inherit" }}
        >
          {raw}
        </Link>
      ) : (
        raw
      ),
    );
    last = re.lastIndex;
  }
  if (last < s.length) parts.push(s.slice(last));
  const body = parts.length ? parts : s;
  return (
    <Typography component="span" sx={{ fontSize: "inherit", fontWeight: "inherit", ...sx }}>
      {body}
    </Typography>
  );
}

function clipText(value: unknown, maxLength: number): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength)}... [truncated]` : trimmed;
}

function compactStringList(value: unknown, maxItems: number, maxLength: number): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => clipText(item, maxLength))
    .filter((item): item is string => Boolean(item))
    .slice(0, maxItems);
}

/** Normalize som-search structured payloads from aggregator-formatted sections for penalty analysis. */
function historicPenaltyFieldsFromSomStructured(structured: Record<string, unknown> | undefined | null): {
  title: string | null;
  description: string | null;
  summary: string | null;
  cfr_citation: string | null;
  requirements: unknown;
  procedures: unknown;
} {
  if (!structured || typeof structured !== "object") {
    return {
      title: null,
      description: null,
      summary: null,
      cfr_citation: null,
      requirements: null,
      procedures: null,
    };
  }

  const nestedIg = structured.interpretive_guidelines as Record<string, unknown> | undefined;
  const bullets =
    nestedIg && Array.isArray(nestedIg.bullets) ? (nestedIg.bullets as unknown[]) : null;

  const requirementsRaw =
    structured.requirements ??
    structured.interpretative_guidelines ??
    bullets ??
    null;

  const surveySteps =
    Array.isArray(structured.survey_procedures) && structured.survey_procedures.length > 0
      ? (structured.survey_procedures as { step?: string }[])
          .map((row) => row?.step)
          .filter((s): s is string => typeof s === "string" && Boolean(s.trim()))
      : null;

  const proceduresRaw = structured.procedures ?? structured.survey_procedures_list ?? surveySteps ?? null;

  const requirementText =
    typeof structured.requirement_text === "string" ? structured.requirement_text.trim() : "";
  const reqObj = structured.requirement as { text?: string } | undefined;
  const requirementFromNested =
    typeof reqObj?.text === "string" ? reqObj.text.trim() : "";
  const requirementBody = requirementText || requirementFromNested || null;

  const title =
    clipText(structured.title, 500) ??
    clipText(structured.section_title, 500) ??
    (typeof structured.tag === "string" && structured.tag.trim()
      ? clipText(
          typeof structured.paragraph_identifier === "string" &&
            structured.paragraph_identifier.trim() &&
            structured.paragraph_identifier !== "N/A"
            ? `${structured.tag.trim()} (${structured.paragraph_identifier.trim()})`
            : structured.tag.trim(),
          500,
        )
      : null);

  const cfrRaw =
    typeof structured.cfrCitation === "string"
      ? structured.cfrCitation
      : typeof structured.cfr_citation === "string"
        ? structured.cfr_citation
        : typeof structured.paragraph_identifier === "string"
          ? structured.paragraph_identifier
          : null;
  const cfr_citation =
    cfrRaw && cfrRaw.trim() && cfrRaw.trim() !== "N/A" ? cfrRaw.trim() : null;

  const igSummary =
    nestedIg && typeof nestedIg.summary === "string" ? nestedIg.summary.trim() : "";
  const summary =
    clipText(structured.summary, 1200) ??
    (igSummary ? clipText(igSummary, 1200) : null) ??
    clipText(requirementBody, 1200);

  const description =
    clipText(structured.description, 1200) ?? clipText(requirementBody, 1200);

  return {
    title,
    description,
    summary,
    cfr_citation,
    requirements: requirementsRaw,
    procedures: proceduresRaw,
  };
}

function compactSomSources(value: unknown): Array<Record<string, unknown> | string> {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 3).map((source) => {
    if (source != null && typeof source === "object") {
      const record = source as Record<string, unknown>;
      return {
        title: clipText(record.title, 200),
        source: clipText(record.source ?? record.file ?? record.url, 300),
      };
    }
    return { source: clipText(String(source), 300) ?? String(source).slice(0, 300) };
  });
}

const HISTORICAL_PENALTY_ANALYSIS_HISTORY_TYPE = "Historical penalty analysis";

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(obj[key])}`)
    .join(",")}}`;
}

function hashString(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function buildHistoricalPenaltyAnalysisHistoryKey(
  facilityContext: EnforcementFacilityContext,
  year: string,
  penalties: EnforcementActionRecord[],
): string {
  const compactPenalties = penalties.map((penalty) => ({
    amount: penalty.amount ?? null,
    amount_formatted: penalty.amount_formatted ?? null,
    case_number: penalty.case_number ?? null,
    date: penalty.date ?? null,
    title: penalty.title ?? null,
    violation_code: penalty.violation_code ?? null,
  }));
  return hashString(
    stableStringify({
      facility_name: facilityContext.facility_name,
      facility_unique_id:
        facilityContext.ccn ??
        facilityContext.ccn_number ??
        facilityContext.facility_unique_id ??
        null,
      state: facilityContext.state,
      year,
      penalties: compactPenalties,
    }),
  );
}

function pickPreventionCard(
  analysis: HistoricalPenaltyAnalysis | undefined,
  penalty: EnforcementActionRecord,
) {
  const cards = analysis?.penalty_to_prevention_cards ?? [];
  if (!cards.length) return undefined;
  const t = (penalty.title || "").toLowerCase();
  return (
    cards.find(
      (c) =>
        (c.penalty_event || "").toLowerCase().includes(t.slice(0, 24)) ||
        t.includes((c.penalty_event || "").toLowerCase().slice(0, 24)),
    ) ?? cards[0]
  );
}

function whatHappenedNarrative(analysis: HistoricalPenaltyAnalysis | undefined, country: string): string {
  const ex = analysis?.executive_summary?.trim() || "";
  if (!ex) return "—";
  const parts = ex.split(/(?<=[.!?])\s+/).filter(Boolean);
  const out = parts.slice(0, 3).join(" ") || ex.slice(0, 400);
  return formatDollarAmountsInText(out, country);
}

/** Descending from end year: first year in range with any monetary penalty under the filter. */
function findLatestYearWithPenalties(
  results: HistoricExportRow[],
  startYear: number,
  endYear: number,
  regulatoryBodyFilter: string,
): { year: number; firstViolationKey: string } | null {
  for (let y = endYear; y >= startYear; y -= 1) {
    const row = results.find((r) => r.year === y);
    if (!row) continue;
    const penalties = getEnforcementPenaltyList(row.data).filter((p) =>
      enforcementRecordMatchesRegulatoryBody(p, regulatoryBodyFilter),
    );
    if (penalties.length > 0) {
      return { year: y, firstViolationKey: `${y}:0` };
    }
  }
  return null;
}

export interface HistoricPenaltiesSectionProps {
  selectedFacility: Record<string, unknown> | null | undefined;
  onSomTagClick?: (tag: string) => void;
  pdcInfoTooltip?: React.ReactNode;
}

const ALL_CITATIONS = "";

export default function HistoricPenaltiesSection({
  selectedFacility,
  onSomTagClick,
  pdcInfoTooltip,
}: HistoricPenaltiesSectionProps) {
  const facilityContext = useMemo(
    () => enforcementContextFromSelectedFacility(selectedFacility ?? undefined),
    [selectedFacility],
  );

  const currentYear = new Date().getFullYear();
  const endYear = currentYear - 1;
  const yearOptions = useMemo(() => {
    const opts: number[] = [];
    for (let y = currentYear - 20; y <= endYear; y += 1) opts.push(y);
    return opts;
  }, [currentYear, endYear]);

  const [startYear, setStartYear] = useState(endYear - 4);
  const [historicSectionExpanded, setHistoricSectionExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<HistoricExportRow[]>([]);
  const [exportPayload, setExportPayload] = useState<{
    startYear: number;
    endYear: number;
    facilityContext: EnforcementFacilityContext;
    rows: HistoricExportRow[];
  } | null>(null);

  const [regulatoryBodyFilter, setRegulatoryBodyFilter] = useState(ALL_REGULATORY_BODIES);
  const [regulatoryOptions, setRegulatoryOptions] = useState<string[]>(() =>
    defaultRegulatoryBodyOptions(facilityContext.state || ""),
  );

  const [activeYear, setActiveYear] = useState(endYear);
  const [citationFilter, setCitationFilter] = useState(ALL_CITATIONS);
  const [selectedViolationKey, setSelectedViolationKey] = useState<string | null>(null);
  const penaltyDetailAnchorRef = useRef<HTMLDivElement | null>(null);

  const [recState, setRecState] = useState<
    Record<
      string,
      { analysis?: HistoricalPenaltyAnalysis; loading: boolean; loaded: boolean; error?: string }
    >
  >({});

  const countryDisplay =
    facilityContext.country || (selectedFacility as { country_code?: string })?.country_code || "USA";

  useEffect(() => {
    setRegulatoryOptions(defaultRegulatoryBodyOptions(facilityContext.state || ""));
    setRegulatoryBodyFilter(ALL_REGULATORY_BODIES);
  }, [facilityContext.state]);

  const loadData = useCallback(async () => {
    setError(null);
    setExportPayload(null);
    if (!facilityContext.facility_name?.trim()) {
      setError("Select a facility with a name to load enforcement history.");
      setRows([]);
      return;
    }
    if (startYear > endYear) {
      setError(`Start year (${startYear}) cannot be after end year (${endYear}).`);
      setRows([]);
      return;
    }

    setLoading(true);
    setRows([]);
    try {
      const years = buildYearRange(startYear, endYear);
      const results = await Promise.all(
        years.map(async (year) => {
          const payload = buildEnforcementSearchRequestPayload(year, facilityContext);
          const data = await facilityComplianceService.postEnforcementSearch(payload);
          return { year, data };
        }),
      );
      setRows(results);
      setExportPayload({
        startYear,
        endYear,
        facilityContext,
        rows: results,
      });
      setRegulatoryOptions(mergeRegulatoryAuthorityFilters(results, facilityContext.state || ""));
      setCitationFilter(ALL_CITATIONS);
      setRecState({});
      const latestWithPenalties = findLatestYearWithPenalties(
        results,
        startYear,
        endYear,
        regulatoryBodyFilter,
      );
      if (latestWithPenalties) {
        setActiveYear(latestWithPenalties.year);
        setSelectedViolationKey(latestWithPenalties.firstViolationKey);
      } else {
        setActiveYear(endYear);
        setSelectedViolationKey(null);
      }
    } catch (e) {
      setRows([]);
      setExportPayload(null);
      setError(e instanceof Error ? e.message : "Failed to load enforcement data.");
    } finally {
      setLoading(false);
    }
  }, [endYear, facilityContext, regulatoryBodyFilter, startYear]);

  useEffect(() => {
    if (!historicSectionExpanded) return undefined;
    const t = window.setTimeout(() => {
      void loadData();
    }, 350);
    return () => window.clearTimeout(t);
  }, [historicSectionExpanded, startYear, loadData]);

  const onHistoricAccordionChange = useCallback((_event: SyntheticEvent, expanded: boolean) => {
    setHistoricSectionExpanded(expanded);
    if (!expanded) {
      setSelectedViolationKey(null);
    }
  }, []);

  const filteredRows = useMemo(() => {
    return rows.map(({ year, data }) => ({
      year,
      data,
      penalties: getEnforcementPenaltyList(data).filter((r) =>
        enforcementRecordMatchesRegulatoryBody(r, regulatoryBodyFilter),
      ),
    }));
  }, [rows, regulatoryBodyFilter]);

  const summary = useMemo(() => {
    let total = 0;
    let count = 0;
    const tagCounts = new Map<string, number>();
    filteredRows.forEach(({ penalties }) => {
      penalties.forEach((p) => {
        count += 1;
        const raw = p.amount;
        const n =
          typeof raw === "number" ? raw : parseFloat(String(raw ?? "").replace(/[^0-9.-]/g, ""));
        if (Number.isFinite(n)) total += n;
        const { tags } = splitViolationCodeIntoTagsAndOther(p.violation_code || "");
        tags.forEach((t) => {
          const k = t.toUpperCase();
          tagCounts.set(k, (tagCounts.get(k) || 0) + 1);
        });
      });
    });
    let mostTag = "—";
    let mostN = 0;
    tagCounts.forEach((n, t) => {
      if (n > mostN) {
        mostN = n;
        mostTag = t;
      }
    });
    return { total, count, mostTag, mostN };
  }, [filteredRows]);

  const maxYearAmount = useMemo(() => {
    let m = 0;
    filteredRows.forEach(({ penalties }) => {
      const s = sumFilteredEnforcementAmount(penalties);
      if (s > m) m = s;
    });
    return m || 1;
  }, [filteredRows]);

  const citationOptions = useMemo(() => {
    const map = new Map<string, string>();
    filteredRows.forEach(({ penalties }) => {
      penalties.forEach((p) => {
        const { tags } = splitViolationCodeIntoTagsAndOther(p.violation_code || "");
        const primary = tags[0]?.toUpperCase() || "";
        if (!primary) return;
        const labelBase = p.title?.trim() || "Citation";
        if (!map.has(primary)) {
          map.set(primary, `${formatSomTagChipLabel(primary)} — ${labelBase.slice(0, 80)}`);
        }
      });
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, label]) => ({ value, label }));
  }, [filteredRows]);

  const citationSelectDisplay = useMemo(() => {
    if (!citationFilter) return "All citations";
    return citationOptions.find((o) => o.value === citationFilter)?.label ?? citationFilter;
  }, [citationFilter, citationOptions]);

  const tableRows = useMemo(() => {
    type Row = {
      key: string;
      year: number;
      penalty: EnforcementActionRecord;
      index: number;
    };
    const out: Row[] = [];
    if (citationFilter) {
      filteredRows.forEach(({ year, penalties }) => {
        penalties.forEach((penalty, index) => {
          const { tags } = splitViolationCodeIntoTagsAndOther(penalty.violation_code || "");
          if (!tags.map((t) => t.toUpperCase()).includes(citationFilter.toUpperCase())) return;
          out.push({ key: `${year}:${index}`, year, penalty, index });
        });
      });
      out.sort((a, b) => String(b.penalty.date || "").localeCompare(String(a.penalty.date || "")));
      return out;
    }
    const block = filteredRows.find((r) => r.year === activeYear);
    (block?.penalties || []).forEach((penalty, index) => {
      out.push({ key: `${activeYear}:${index}`, year: activeYear, penalty, index });
    });
    return out;
  }, [filteredRows, activeYear, citationFilter]);

  const loadRecommendations = useCallback(
    async (year: string, penalties: EnforcementActionRecord[], stateKey: string) => {
      setRecState((prev) => ({
        ...prev,
        [stateKey]: { ...prev[stateKey], loading: true, error: undefined },
      }));
      try {
        const historyKey = buildHistoricalPenaltyAnalysisHistoryKey(
          facilityContext,
          year,
          penalties,
        );
        const userIdRaw = getCurrentUserId();
        const userId = userIdRaw ? Number.parseInt(userIdRaw, 10) : 0;
        const selectedFacilityIdRaw = selectedFacility?.facility_id;
        const facilityId =
          typeof selectedFacilityIdRaw === "number"
            ? selectedFacilityIdRaw
            : Number.parseInt(String(selectedFacilityIdRaw || ""), 10);

        if (Number.isFinite(facilityId) && facilityId > 0) {
          try {
            const history = await continuousComplianceService.getAnalysisHistory({
              facility_id: facilityId,
              type: HISTORICAL_PENALTY_ANALYSIS_HISTORY_TYPE,
              page: 1,
              page_size: 50,
            });
            const existing = history.data.find(
              (item) =>
                (item?.data_obj?.request_key === historyKey ||
                  (!item?.data_obj?.request_key && item?.data_obj?.analysis_year === Number(year))) &&
                item?.data_obj?.analysis,
            );
            if (existing?.data_obj?.analysis) {
              setRecState((prev) => ({
                ...prev,
                [stateKey]: {
                  analysis: existing.data_obj.analysis as HistoricalPenaltyAnalysis,
                  loading: false,
                  loaded: true,
                },
              }));
              return;
            }
          } catch (historyError) {
            console.warn("Failed to load historical penalty analysis history.", historyError);
          }
        }

        const seenTags = new Set<string>();
        penalties.forEach((penalty) => {
          const { tags } = splitViolationCodeIntoTagsAndOther(penalty.violation_code || "");
          tags.forEach((tag) => seenTags.add(tag.toUpperCase()));
        });

        const somTagContext: HistoricalPenaltySomTagContext[] = await Promise.all(
          [...seenTags].map(async (tag) => {
            try {
              const somResponse: SomSearchResponse = await riskAnalysisService.somSearch({
                query: tag,
                category: "VectorStores_usa_healthcare_manual_som107ap_g_rhc",
                customer_id: "1",
                org_name: "test",
                user_persona: "survey_analyst",
              });
              const structured = somResponse.structured as Record<string, unknown> | undefined;
              const extracted = historicPenaltyFieldsFromSomStructured(structured);
              const sources = compactSomSources(somResponse.sources);
              return {
                tag,
                title: clipText(extracted.title, 200),
                description: clipText(extracted.description, 1200),
                cfr_citation: extracted.cfr_citation,
                summary: clipText(extracted.summary, 1200),
                requirements: compactStringList(extracted.requirements, 6, 350),
                procedures: compactStringList(extracted.procedures, 6, 350),
                structured: null,
                sources,
                query_result: clipText(somResponse.result, 1200),
              };
            } catch (e) {
              return {
                tag,
                error: e instanceof Error ? e.message : "SOM tag lookup failed",
              };
            }
          }),
        );

        const response = await facilityComplianceService.postHistoricalPenaltyAnalysis({
          facility: {
            facility_name: facilityContext.facility_name,
            city: facilityContext.city || null,
            state: facilityContext.state || null,
            county: facilityContext.county || null,
            country: facilityContext.country || countryDisplay || "USA",
            facility_type:
              typeof selectedFacility?.facility_type === "string"
                ? selectedFacility.facility_type
                : null,
            facility_type_id: facilityContext.facility_type_id ?? null,
          },
          analysis_year: Number(year),
          as_of_date: new Date().toISOString().slice(0, 10),
          penalties,
          som_tag_context: somTagContext,
        });

        if (userId > 0 && Number.isFinite(facilityId) && facilityId > 0) {
          try {
            await continuousComplianceService.saveAnalysisHistory({
              user_id: userId,
              facility_id: facilityId,
              type: HISTORICAL_PENALTY_ANALYSIS_HISTORY_TYPE,
              data_obj: {
                request_key: historyKey,
                analysis_year: Number(year),
                facility_name: facilityContext.facility_name,
                penalty_count: penalties.length,
                analysis: response.analysis,
                metadata: response.metadata,
              },
            });
          } catch (historyError) {
            console.warn("Failed to save historical penalty analysis history.", historyError);
          }
        }

        setRecState((prev) => ({
          ...prev,
          [stateKey]: { analysis: response.analysis, loading: false, loaded: true },
        }));
      } catch (e) {
        setRecState((prev) => ({
          ...prev,
          [stateKey]: {
            loading: false,
            loaded: false,
            error: e instanceof Error ? e.message : "Historical penalty analysis failed.",
          },
        }));
      }
    },
    [countryDisplay, facilityContext, selectedFacility],
  );

  const selectedPenaltyDetail = useMemo(() => {
    if (!selectedViolationKey) return null;
    const [y, idx] = selectedViolationKey.split(":");
    const yearNum = Number(y);
    const index = Number(idx);
    const block = filteredRows.find((r) => r.year === yearNum);
    const penalty = block?.penalties[index];
    if (!penalty) return null;
    return { year: yearNum, index, penalty, stateKey: selectedViolationKey };
  }, [selectedViolationKey, filteredRows]);

  useEffect(() => {
    if (!selectedViolationKey) return;
    const id = window.requestAnimationFrame(() => {
      penaltyDetailAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedViolationKey]);

  /** Year-tab view keys rows as `${activeYear}:${index}`; drop stale selection if it ever disagrees. */
  useEffect(() => {
    if (citationFilter || !selectedViolationKey) return;
    const yearPart = Number(selectedViolationKey.split(":")[0]);
    if (!Number.isFinite(yearPart) || yearPart !== activeYear) {
      setSelectedViolationKey(null);
    }
  }, [activeYear, citationFilter, selectedViolationKey]);

  const downloadExcel = useCallback(async () => {
    if (!exportPayload?.rows?.length) return;
    const fc = exportPayload.facilityContext;
    const facilityName = fc.facility_name || "Facility";
    const locationStr = [fc.city, fc.state, fc.country || "USA"].filter((p) => p && String(p).trim()).join(", ");
    const country = fc.country || "USA";
    const sortedRows = [...exportPayload.rows].sort((a, b) => a.year - b.year);
    const incidents: Array<{
      year: number;
      penalty: EnforcementActionRecord;
      tagSplit: ReturnType<typeof splitViolationCodeIntoTagsAndOther>;
    }> = [];
    const tagSet = new Set<string>();
    sortedRows.forEach((row) => {
      const list = getEnforcementPenaltyList(row.data).filter((p) =>
        enforcementRecordMatchesRegulatoryBody(p, regulatoryBodyFilter),
      );
      list.forEach((penalty) => {
        const split = splitViolationCodeIntoTagsAndOther(penalty.violation_code || "");
        split.tags.forEach((t) => tagSet.add(t));
        incidents.push({ year: row.year, penalty, tagSplit: split });
      });
    });
    const workbook = new ExcelJS.Workbook();
    const incidentSheet = workbook.addWorksheet("Incidents");
    incidentSheet.addRow([
      "Facility Name",
      "Location",
      "Year",
      "Incident title",
      "Amount (numeric)",
      "Amount (display)",
      "Date",
      "Authority",
      "Severity",
      "Description",
      "Violation code",
      "Deficiency tags",
      "Case number",
      "Data lineage",
    ]);
    incidents.forEach((item) => {
      const penalty = item.penalty;
      const rawAmt = penalty.amount;
      const numAmt =
        typeof rawAmt === "number"
          ? rawAmt
          : parseFloat(String(rawAmt != null ? rawAmt : "").replace(/[^0-9.-]/g, ""));
      const dispAmt =
        penalty.amount_formatted ||
        (rawAmt != null && !(typeof rawAmt === "string" && rawAmt.trim() === "")
          ? formatHistoricalCurrency(rawAmt, country)
          : "N/A");
      const lineageLabel = isRegulatoryOpenDataArchiveRecord(penalty)
        ? `OPEN_DATA / regulatory_facility_event (id=${String(penalty.raw_data?.["regulatory_event_id"] ?? "")})`
        : "";
      incidentSheet.addRow([
        facilityName,
        locationStr,
        item.year,
        penalty.title || "Enforcement Action",
        Number.isNaN(numAmt) ? "" : numAmt,
        dispAmt,
        penalty.date || "",
        penalty.issuing_authority || "",
        penalty.severity || "",
        truncateForExcelCell(penalty.description || ""),
        penalty.violation_code || "",
        item.tagSplit.tags.join(", "),
        penalty.case_number || "",
        lineageLabel,
      ]);
    });
    const refSheet = workbook.addWorksheet("Unique deficiency tags");
    refSheet.addRow(["Deficiency tag"]);
    Array.from(tagSet)
      .sort((a, b) => a.localeCompare(b))
      .forEach((t) => refSheet.addRow([t]));
    const summarySheet = workbook.addWorksheet("Year summary");
    summarySheet.addRow(["Year", "Actions", "Monetary total (numeric)", "Monetary total (display)"]);
    sortedRows.forEach((row) => {
      const list = getEnforcementPenaltyList(row.data).filter((p) =>
        enforcementRecordMatchesRegulatoryBody(p, regulatoryBodyFilter),
      );
      const totalAmount = sumFilteredEnforcementAmount(list);
      summarySheet.addRow([
        row.year,
        list.length,
        totalAmount,
        formatHistoricalCurrency(totalAmount, country),
      ]);
    });
    incidentSheet.getRow(1).font = { bold: true };
    refSheet.getRow(1).font = { bold: true };
    summarySheet.getRow(1).font = { bold: true };
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeName = String(facilityName).replace(/[/\\:*?"<>|]/g, "_").trim().slice(0, 80) || "facility";
    const ts = new Date().toISOString().slice(0, 19).replace(/[:-]/g, "");
    link.download = `${safeName}_Historical_Enforcement_${exportPayload.startYear}_${exportPayload.endYear}_${ts}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [exportPayload, regulatoryBodyFilter]);

  const globalEmpty =
    !loading &&
    !error &&
    rows.length > 0 &&
    filteredRows.every((r) => r.penalties.length === 0);

  return (
    <StyledAccordion
      expanded={historicSectionExpanded}
      onChange={onHistoricAccordionChange}
      sx={{ width: "100%", minWidth: 0 }}
    >
      <AccordionSummary
        expandIcon={expandIconSvg}
        sx={{
          alignItems: "center",
          borderRadius: "4px",
          border: "1px solid #9EE5F4",
          backgroundColor: "#FFF",
          boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": { transform: "rotate(180deg)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", minWidth: 0, pr: 0.5 }}>
          {historicPenaltiesLeadingIcon}
          <AccordionTitle className="accordion-title" sx={{ flex: 1, minWidth: 0 }}>
            Historic Penalties & Recommendations
          </AccordionTitle>
          {pdcInfoTooltip ? (
            <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
              {pdcInfoTooltip}
            </Box>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: "#ffffff", padding: "12px", position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
            p: 1.5,
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            bgcolor: "#fafafa",
            alignItems: "center",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="historic-start-year-label">Start year</InputLabel>
            <Select
              labelId="historic-start-year-label"
              label="Start year"
              value={startYear}
              onChange={(e) => setStartYear(Number(e.target.value))}
              sx={{ fontFamily: "Rethink Sans, sans-serif", bgcolor: "#fff" }}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: "14px", color: "#6b7280", fontFamily: "Rethink Sans, sans-serif" }}>
            through last calendar year
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="historic-reg-body-label">Regulatory body</InputLabel>
            <Select
              labelId="historic-reg-body-label"
              label="Regulatory body"
              value={regulatoryBodyFilter}
              onChange={(e) => {
                setRegulatoryBodyFilter(String(e.target.value));
                setSelectedViolationKey(null);
              }}
              sx={{ fontFamily: "Rethink Sans, sans-serif", bgcolor: "#fff" }}
            >
              {regulatoryOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void loadData()}
            disabled={loading}
            sx={{ borderColor: "#065b70", color: "#065b70", textTransform: "none" }}
          >
            Refresh
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
            <CircularProgress size={24} sx={{ color: "#065b70" }} />
            <Typography sx={{ fontSize: "14px", color: "#065b70", fontFamily: "Rethink Sans, sans-serif" }}>
              Loading enforcement history for {startYear}–{endYear}…
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Box sx={{ p: 2, borderRadius: 1, backgroundColor: "#fef2f2", border: "1px solid #fecaca" }}>
            <Typography sx={{ fontSize: "14px", color: "#9a3412", fontFamily: "Rethink Sans, sans-serif", mb: 1 }}>
              {error}
            </Typography>
            <Button size="small" variant="contained" onClick={() => void loadData()}>
              Retry
            </Button>
          </Box>
        )}

        {!loading && !error && rows.length === 0 && facilityContext.facility_name?.trim() && (
          <Typography sx={{ fontSize: "14px", color: "#6b7280", fontFamily: "Rethink Sans, sans-serif" }}>
            Open this section to load data. Adjust start year or regulatory body as needed.
          </Typography>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            {globalEmpty ? (
              <Box sx={{ p: 3, border: "1px solid #e5e7eb", borderRadius: 2, textAlign: "center" }}>
                <Typography sx={{ fontWeight: 600, mb: 1, fontFamily: "Rethink Sans, sans-serif" }}>
                  No penalties in selected period
                </Typography>
                <Typography sx={{ color: "#6b7280", fontFamily: "Rethink Sans, sans-serif" }}>
                  No documented monetary penalties matched <strong>{regulatoryBodyFilter}</strong> between{" "}
                  <strong>
                    {startYear}–{endYear}
                  </strong>{" "}
                  for <strong>{facilityContext.facility_name}</strong>.
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.06em" }}>
                      TOTAL PENALTIES
                    </Typography>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#dc2626" }}>
                      {formatHistoricalCurrency(summary.total, countryDisplay)}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>across selected period</Typography>
                  </Box>
                  <Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.06em" }}>
                      VIOLATIONS
                    </Typography>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#111827" }}>{summary.count}</Typography>
                  </Box>
                  <Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.06em" }}>
                      MOST CITED
                    </Typography>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "#111827" }}>
                      {summary.mostTag}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                      {summary.mostN ? `${summary.mostN} citation(s)` : "—"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2, p: 2, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      color: "#065b70",
                      mb: 1.5,
                    }}
                  >
                    PENALTY TREND
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Year</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Penalty amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Violations
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...filteredRows]
                        .sort((a, b) => b.year - a.year)
                        .map(({ year, penalties }) => {
                          const amt = sumFilteredEnforcementAmount(penalties);
                          const pct = maxYearAmount ? Math.min(100, Math.round((amt / maxYearAmount) * 100)) : 0;
                          return (
                            <TableRow key={year}>
                              <TableCell>{year}</TableCell>
                              <TableCell sx={{ minWidth: 160 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  sx={{
                                    height: 10,
                                    borderRadius: 1,
                                    bgcolor: "#e5e7eb",
                                    "& .MuiLinearProgress-bar": { bgcolor: "#34788c" },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">{penalties.length}</TableCell>
                              <TableCell align="right" sx={{ color: "#dc2626", fontWeight: 700 }}>
                                {formatHistoricalCurrency(amt, countryDisplay)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Tabs
                    value={activeYear}
                    onChange={(_, v) => {
                      setActiveYear(v as number);
                      setCitationFilter(ALL_CITATIONS);
                      setSelectedViolationKey(null);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      opacity: citationFilter ? 0.45 : 1,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontFamily: "Rethink Sans, sans-serif",
                        border: "1px solid #e5e7eb",
                        borderRadius: 1,
                        mr: 0.5,
                      },
                      "& .Mui-selected": { bgcolor: "#065b70", color: "#fff !important" },
                    }}
                  >
                    {[...filteredRows]
                      .map((r) => r.year)
                      .sort((a, b) => b - a)
                      .map((y) => (
                        <Tab key={y} label={String(y)} value={y} disabled={Boolean(citationFilter)} />
                      ))}
                  </Tabs>
                  <FormControl size="small" sx={{ minWidth: 220, maxWidth: { xs: "100%", sm: 420 }, flex: { sm: "0 1 auto" } }}>
                    <InputLabel id="citation-filter-label">Citation</InputLabel>
                    <Select
                      labelId="citation-filter-label"
                      label="Citation"
                      value={citationFilter || ALL_CITATIONS}
                      onChange={(e) => {
                        const v = String(e.target.value);
                        setCitationFilter(v);
                        setSelectedViolationKey(null);
                      }}
                      renderValue={() => (
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: "Rethink Sans, sans-serif",
                            fontSize: "1rem",
                            fontWeight: 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            width: "100%",
                          }}
                        >
                          {citationSelectDisplay}
                        </Typography>
                      )}
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        bgcolor: "#fff",
                        "& .MuiSelect-select": { display: "flex", alignItems: "center", overflow: "hidden" },
                      }}
                    >
                      <MenuItem value={ALL_CITATIONS}>All citations</MenuItem>
                      {citationOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value} sx={{ whiteSpace: "nowrap" }}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path
                          d="M12 3v12m0 0l4-4m-4 4l-4-4M5 21h14"
                          stroke="#065b70"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    }
                    onClick={() => void downloadExcel()}
                    disabled={!exportPayload?.rows?.length || loading}
                    sx={{ borderColor: "#065b70", color: "#065b70", textTransform: "none", ml: "auto" }}
                  >
                    Download Excel
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#9ca3af", mb: 1, fontFamily: "Rethink Sans, sans-serif" }}
                >
                  <span style={{ display: "none" }}>
                    Monetary penalties, fines {activeYear}
                  </span>
                  {citationFilter
                    ? `Showing all years for citation filter · ${filteredRows.length} year(s) in range`
                    : `Monetary penalties, fines ${activeYear}`}
                </Typography>

                <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 2, mb: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#f9fafb" }}>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Regulatory citation</TableCell>
                        <TableCell align="right">Penalty amount</TableCell>
                        <TableCell>Authority</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Case / Ref</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.map(({ key, year, penalty }) => {
                        const active = selectedViolationKey === key;
                        const { tags } = splitViolationCodeIntoTagsAndOther(penalty.violation_code || "");
                        const tag = tags[0];
                        const sev = penalty.severity || "Moderate";
                        const sc = severityChipColor(sev);
                        return (
                          <TableRow
                            key={key}
                            hover
                            selected={active}
                            onClick={() => {
                              setSelectedViolationKey((prev) => (prev === key ? null : key));
                            }}
                            sx={{ cursor: "pointer", "&.Mui-selected": { bgcolor: "#e0f2fe !important" } }}
                          >
                            <TableCell>{penalty.date || "—"}</TableCell>
                            <TableCell sx={{ maxWidth: { xs: 280, sm: 520 } }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "nowrap", minWidth: 0 }}>
                                {tag ? (
                                  <Chip
                                    label={formatSomTagChipLabel(tag)}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSomTagClick?.(tag);
                                    }}
                                    sx={{
                                      flexShrink: 0,
                                      height: 22,
                                      fontSize: "10px",
                                      bgcolor: "#e0f4f8",
                                      color: "#065b70",
                                    }}
                                  />
                                ) : null}
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "13px",
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {penalty.title || "—"}
                                </Typography>
                              </Box>
                              {isRegulatoryOpenDataArchiveRecord(penalty) ? (
                                <Chip
                                  label="Archive import"
                                  size="small"
                                  sx={{ ml: 1, height: 20, fontSize: "10px", bgcolor: "#f3f4f6", color: "#374151" }}
                                />
                              ) : null}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "#dc2626", fontWeight: 700 }}>
                              {enforcementPenaltyAmount(penalty, countryDisplay)}
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: "13px" }}>{penalty.issuing_authority || "—"}</Typography>
                              {penalty.regulator ? (
                                <Chip
                                  label={penalty.regulator}
                                  size="small"
                                  sx={{ mt: 0.5, height: 20, fontSize: "10px" }}
                                />
                              ) : null}
                              {penalty.case_type ? (
                                <Chip
                                  label={penalty.case_type}
                                  size="small"
                                  sx={{ mt: 0.5, height: 20, fontSize: "10px", ml: penalty.regulator ? 0.5 : 0 }}
                                />
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Chip label={sev} size="small" sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600 }} />
                            </TableCell>
                            <TableCell>{penalty.case_number || "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {selectedPenaltyDetail ? (
                  <Box
                    ref={penaltyDetailAnchorRef}
                    sx={{ scrollMarginTop: { xs: 12, sm: 16 } }}
                  >
                    <ViolationDetailPanel
                      key={selectedViolationKey}
                      detail={selectedPenaltyDetail}
                      countryDisplay={countryDisplay}
                      rows={rows}
                      recState={recState}
                      loadRecommendations={loadRecommendations}
                      onSomTagClick={onSomTagClick}
                    />
                  </Box>
                ) : null}
              </>
            )}
          </>
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
}

function ViolationDetailPanel({
  detail,
  countryDisplay,
  rows,
  recState,
  loadRecommendations,
  onSomTagClick,
}: {
  detail: { year: number; index: number; penalty: EnforcementActionRecord; stateKey: string };
  countryDisplay: string;
  rows: HistoricExportRow[];
  recState: Record<
    string,
    { analysis?: HistoricalPenaltyAnalysis; loading: boolean; loaded: boolean; error?: string }
  >;
  loadRecommendations: (y: string, p: EnforcementActionRecord[], k: string) => Promise<void>;
  onSomTagClick?: (tag: string) => void;
}) {
  const { penalty, year, stateKey } = detail;
  const yst = recState[stateKey];
  const analysis = yst?.analysis;
  const card = pickPreventionCard(analysis, penalty);
  const { tags, otherParts } = splitViolationCodeIntoTagsAndOther(penalty.violation_code || "");
  const primaryTag = tags[0];
  const [tab, setTab] = useState(0);
  const dataForYear = rows.find((r) => r.year === year)?.data;
  const regulationLine =
    otherParts.join(", ") ||
    (card?.regulation as string | undefined) ||
    penalty.violation_code ||
    "—";

  const fmt$ = (s: string | undefined | null) =>
    s == null || s === "" ? "" : formatDollarAmountsInText(String(s), countryDisplay);

  useEffect(() => {
    const s = recState[stateKey];
    if (s?.loading || s?.loaded || s?.error) return;
    void loadRecommendations(String(year), [penalty], stateKey);
  }, [stateKey, year, penalty, loadRecommendations, recState]);

  const detailHeaderLabelSx = {
    fontSize: "11px",
    fontWeight: 700,
    color: "#6b7280",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  } as const;
  const detailHeaderValueSx = { fontSize: "13px", fontWeight: 700, lineHeight: 1.35 } as const;

  return (
    <Box sx={{ border: "1px solid #d9eef3", borderRadius: 2, overflow: "hidden", mb: 2 }}>
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          flexWrap: "wrap",
          gap: 2.5,
          alignItems: "flex-start",
          justifyContent: "space-between",
          bgcolor: "#fbfdfe",
        }}
      >
        <Box
          sx={{
            flex: "1 1 260px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={detailHeaderLabelSx}>TAG</Typography>
            <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center" }}>
              {primaryTag ? (
                <Chip
                  label={formatSomTagChipLabel(primaryTag)}
                  size="small"
                  sx={{ bgcolor: "#e0f4f8" }}
                  onClick={() => onSomTagClick?.(primaryTag)}
                />
              ) : (
                <Typography sx={detailHeaderValueSx}>—</Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={detailHeaderLabelSx}>REGULATION</Typography>
            <TextWithCfrLinks
              text={fmt$(regulationLine)}
              sx={{
                mt: 0.5,
                fontSize: "13px",
                fontWeight: 700,
                display: "block",
                lineHeight: 1.45,
                color: "#111827",
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            flex: "1 1 200px",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={detailHeaderLabelSx}>AUTHORITY</Typography>
            <Typography sx={{ ...detailHeaderValueSx, mt: 0.5 }}>{penalty.issuing_authority || "—"}</Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={detailHeaderLabelSx}>PENALTY TYPE</Typography>
            <Typography sx={{ ...detailHeaderValueSx, mt: 0.5 }}>
              {penalty.title || penalty.case_type || "—"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: "0 0 auto", minWidth: "min-content", alignSelf: "flex-start" }}>
          <Typography sx={detailHeaderLabelSx}>PENALTY AMOUNT</Typography>
          <Typography
            sx={{
              fontSize: "1.1rem",
              fontWeight: 800,
              color: "#dc2626",
              mt: 0.5,
              lineHeight: 1.2,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {enforcementPenaltyAmount(penalty, countryDisplay)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2, pt: 1 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v as number)} sx={{ minHeight: 40 }}>
          <Tab label="What happened" sx={{ textTransform: "none", fontSize: "13px" }} />
          <Tab label="Root cause" sx={{ textTransform: "none", fontSize: "13px" }} />
          <Tab label="Controls & evidence" sx={{ textTransform: "none", fontSize: "13px" }} />
          <Tab label="Owner & escalation" sx={{ textTransform: "none", fontSize: "13px" }} />
        </Tabs>
      </Box>
      <Box sx={{ p: 2 }}>
        {yst?.loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, mb: 1 }}>
            <CircularProgress size={22} />
            <Typography sx={{ fontSize: "13px" }}>Analyzing…</Typography>
          </Box>
        ) : null}
        {yst?.error ? <Typography sx={{ color: "#9a3412", mb: 1 }}>{fmt$(yst.error)}</Typography> : null}
        <>
            {isGlobalAdministrator() && analysis?.partial_analysis_warnings?.length && tab === 0 ? (
              <Box sx={{ p: 1.5, mb: 2, bgcolor: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 1 }}>
                {analysis.partial_analysis_warnings.map((w, i) => (
                  <Typography key={i} sx={{ fontSize: "13px", color: "#9a3412" }}>
                    {fmt$(w)}
                  </Typography>
                ))}
              </Box>
            ) : null}
            {tab === 0 && (
              <Box>
                <Typography sx={{ fontSize: "14px", color: "#374151", lineHeight: 1.55, mb: analysis ? 2 : 0 }}>
                  {fmt$(penalty.description || "—")}
                </Typography>
                {analysis ? (
                  <>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#065b70", mb: 0.5 }}>
                      Executive summary
                    </Typography>
                    <Typography sx={{ fontSize: "14px", color: "#374151", lineHeight: 1.55, mb: 2 }}>
                      {whatHappenedNarrative(analysis, countryDisplay)}
                    </Typography>
                    {analysis.calendar_alerts?.map((a, i) => (
                      <Typography key={i} sx={{ fontSize: "13px", fontWeight: 600, mb: 0.5 }}>
                        {fmt$(a.title)}
                      </Typography>
                    ))}
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>PENALTY TYPE</Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {`${[penalty.title, penalty.case_type].filter(Boolean).join(" · ") || "Civil monetary penalty"} · Fine`}
                      </Typography>
                    </Box>
                  </>
                ) : null}
              </Box>
            )}
            {tab === 1 && (
              <Typography sx={{ fontSize: "14px", color: "#374151", lineHeight: 1.55 }}>
                {fmt$(card?.root_cause || analysis?.executive_summary || penalty.description || "—")}
              </Typography>
            )}
            {tab === 2 && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Required controls</Typography>
                  <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                    {(card?.required_controls || []).map((c, i) => (
                      <li key={i}>
                        <Typography sx={{ fontSize: "13px" }}>{fmt$(c)}</Typography>
                      </li>
                    ))}
                    {!card?.required_controls?.length ? (
                      <Typography sx={{ color: "#6b7280" }}>—</Typography>
                    ) : null}
                  </ul>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>Evidence needed</Typography>
                  <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                    {(card?.evidence_needed || []).map((c, i) => (
                      <li key={i}>
                        <Typography sx={{ fontSize: "13px" }}>{fmt$(c)}</Typography>
                      </li>
                    ))}
                    {!card?.evidence_needed?.length ? (
                      <Typography sx={{ color: "#6b7280" }}>—</Typography>
                    ) : null}
                  </ul>
                </Box>
              </Box>
            )}
            {tab === 3 && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Owner</Typography>
                  <Typography>{fmt$(card?.owner || "—")}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Escalation rule</Typography>
                  <Typography>{fmt$(card?.executive_escalation || "—")}</Typography>
                </Box>
              </Box>
            )}
        </>
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          bgcolor: "#fafafa",
        }}
      >
        <Typography sx={{ fontSize: "13px", color: "#374151" }}>
          Case / Ref: {penalty.case_number || "—"}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#6b7280", lineHeight: 1.45 }}>
          <strong>Sources:</strong>{" "}
          {buildHistoricEnforcementSourcesDisplayText(dataForYear)}{" "}
          · Searched {formatEnforcementSearchedDateLabel(dataForYear || {})}
        </Typography>
      </Box>
    </Box>
  );
}
